import {
  Button,
  Center,
  Group,
  Loader,
  Modal,
  Tabs,
  type TabsTabProps
} from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router";
import api from "../../api";
import { useMissingObjectData } from "../../hooks/useMissingObjectData";
import { usePersons } from "../../hooks/usePersons";
import { TabWithHighlightAndError } from "../../mantine/components/TabWithHighlightAndError/TabWithHighlightAndError";
import { useToast } from "../../mantine/notifications/useToast";
import type { BaseWizardProps } from "../../types/wizard.types";
import urls from "../../urls";
import { MeasurementType } from "../../utils/enums";
import { CounterDataView } from "../CounterDataView/CounterDataView";
import { LoadOrError } from "../LoadOrError/LoadOrError";
import type { GasConnectionResponse } from "./GasConnection.types";
import { GasConnectionForm } from "./GasConnectionForm/GasConnectionForm";
import type { GasConnectionFormData } from "./GasConnectionForm/GasConnectionForm.types";
import { useGasConnection } from "./hooks/useGasConnection";
import { useGasConnectionMutation } from "./hooks/useGasConnectionMutation";

type TabProps = TabsTabProps & { label: string };

const TABS: Array<TabProps> = [
  {
    value: "general",
    label: "Stammdaten"
  },
  {
    value: "messwerte",
    label: "Messwerte"
  }
];

enum CreateStep {
  CreateForm = "create-form",
  GasConnectionData = "gas-connection-data"
}

type GasConnectionWizardProps = BaseWizardProps & {
  siteId: number;
  variantId: number;
};

function GasConnectionWizard({
  graphCoordinates = { x: 0, y: 0 },
  isOpen,
  siteId,
  variantId,
  onClose,
  ...modeProps
}: GasConnectionWizardProps) {
  const queryClient = useQueryClient();
  const { showToast, showErrorToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const { state: locationState } = useLocation();

  const gasConnectionId =
    modeProps.mode === "edit" ? modeProps.objectId : undefined;

  const {
    data: gasConnection,
    isLoading: isGasConnectionLoading,
    error: gasConnectionError
  } = useGasConnection(gasConnectionId);

  const {
    isLoading: isMissingDataLoading,
    error: missingDataError,
    fieldsThatCouldBeMissing,
    shouldFieldBeHighlighted
  } = useMissingObjectData(gasConnectionId, siteId);

  const [createModeStep, setCreateModeStep] = useState(CreateStep.CreateForm);
  const [newGasConnectionData, setNewGasConnectionData] = useState<
    GasConnectionResponse | undefined
  >(undefined);
  const [counterUpdateRequestData, setCounterUpdateRequestData] = useState({});
  const [isSavingGasConnectionData, setIsSavingGasConnectionData] =
    useState(false);
  const { createMutation, editMutation } = useGasConnectionMutation(siteId);
  const {
    persons,
    isLoading: isLoadingPersons,
    error: errorPersons
  } = usePersons({ variantId });
  const [numErrors, setNumErrors] = useState(0);
  const [highlightedTabs, setHighlightedTabs] = useState<Array<string>>([]);

  useEffect(() => {
    if (!isOpen) {
      setCreateModeStep(CreateStep.CreateForm);
      setNewGasConnectionData(undefined);
      setCounterUpdateRequestData({});
      setNumErrors(0);
      setHighlightedTabs([]);
    }
  }, [isOpen]);

  async function handleSubmitCreate(formData: GasConnectionFormData) {
    return createMutation.mutateAsync(
      {
        ...formData,
        site: siteId,
        sketch_element: {
          x_position: graphCoordinates.x,
          y_position: graphCoordinates.y
        }
      },
      {
        onSuccess: (response) => {
          setNewGasConnectionData(response.data);
          setCreateModeStep(CreateStep.GasConnectionData);
        }
      }
    );
  }

  async function handleSubmitUpdate(
    formData: GasConnectionFormData,
    id: number
  ) {
    return editMutation.mutateAsync(
      { ...formData, id },
      {
        onSuccess: async (response) => {
          await handleSaveGasConnectionData();
          await queryClient.invalidateQueries({
            queryKey: [
              "counterDataViewData",
              { dataUrl: urls.api.gasConnectionOption(response.data.id) }
            ]
          });

          onClose();
        }
      }
    );
  }

  async function handleSaveGasConnectionData() {
    setIsSavingGasConnectionData(true);

    const promises = Object.keys(counterUpdateRequestData).map((counterId) => {
      const url = urls.api.counterDataForGasConnection(parseInt(counterId, 10));
      return api.patch(url, counterUpdateRequestData[counterId]);
    });

    await Promise.all(promises)
      .then(async () => {
        if (modeProps.mode === "create") {
          if (newGasConnectionData) {
            await queryClient.invalidateQueries({
              queryKey: [
                "counterDataViewData",
                {
                  dataUrl: urls.api.gasConnectionOption(newGasConnectionData.id)
                }
              ]
            });
          }
          handleGasConnectionDataCreateModeStepDone();
        } else {
          showToast({
            type: "success",
            message: "Die Änderungen wurden gespeichert."
          });
        }
      })
      .catch((error) => {
        showErrorToast(error);
      })
      .finally(() => setIsSavingGasConnectionData(false));
  }

  const handleChangeNumErrors = useCallback((numErrors: number) => {
    setNumErrors(numErrors);
  }, []);

  const handleChangeNumHighlightedFields = useCallback(
    (numHighlightedFields: number) => {
      setHighlightedTabs(numHighlightedFields > 0 ? [TABS[0].value] : []);
    },
    []
  );

  function handleGasConnectionDataCreateModeStepDone() {
    onClose();
    showToast({
      type: "success",
      message: "Der Gas-Netzanschlusspunkt wurde erstellt."
    });
  }

  function getActiveTab() {
    if (modeProps.mode === "edit") {
      const requestedTab = searchParams.get("tab") ?? TABS[0].value;
      const requestedTabIsValid = TABS.some(
        (tab) => tab.value === requestedTab
      );
      return requestedTabIsValid ? requestedTab : TABS[0].value;
    }

    return createModeStep === CreateStep.CreateForm
      ? TABS[0].value
      : TABS[1].value;
  }

  const activeTab = getActiveTab();
  const isEditMode = modeProps.mode === "edit";
  const showTabs = isEditMode;
  const showGasConnectionForm =
    createModeStep === CreateStep.CreateForm || isEditMode;
  const showGasConnectionData =
    isEditMode || createModeStep === CreateStep.GasConnectionData;
  const activeGasConnectionId = isEditMode
    ? modeProps.objectId
    : newGasConnectionData?.id;

  const isLoading =
    isEditMode &&
    (isGasConnectionLoading || isMissingDataLoading || isLoadingPersons);
  const error = isEditMode
    ? gasConnectionError || missingDataError || errorPersons
    : null;

  const title = gasConnection?.name
    ? `${gasConnection.name} bearbeiten`
    : isEditMode
      ? "Gas-Netzanschlusspunkt bearbeiten"
      : "Gas-Netzanschlusspunkt erstellen";

  function getFormModeProps() {
    if (modeProps.mode === "create") {
      return { mode: "create" as const };
    }

    if (gasConnection) {
      return {
        mode: "edit" as const,
        wizardObject: gasConnection,
        fieldsThatCouldBeMissing,
        shouldFieldBeHighlighted
      };
    }

    return null;
  }

  const formModeProps = getFormModeProps();

  return (
    <Modal
      className="GasConnectionWizard"
      opened={isOpen}
      size="xl"
      title={title}
      onClose={onClose}
    >
      <LoadOrError error={error} loading={isLoading}>
        <Tabs
          value={activeTab}
          onChange={(newTab) => {
            if (!newTab) {
              return;
            }

            searchParams.set("tab", newTab);
            setSearchParams(searchParams, { state: locationState });
          }}
        >
          {showTabs && (
            <Tabs.List>
              {TABS.map((tab, index) => (
                <TabWithHighlightAndError
                  component="a"
                  highlighted={highlightedTabs.includes(tab.value)}
                  key={tab.value}
                  numErrors={index === 0 ? numErrors : 0}
                  value={tab.value}
                >
                  {tab.label}
                </TabWithHighlightAndError>
              ))}
            </Tabs.List>
          )}
          {showGasConnectionForm && (
            <Tabs.Panel value={TABS[0].value}>
              <LoadOrError
                error={errorPersons}
                loading={isLoadingPersons && !isEditMode}
              >
                {formModeProps && persons && (
                  <GasConnectionForm
                    persons={persons}
                    onCancel={onClose}
                    onChangeNumErrors={handleChangeNumErrors}
                    onChangeNumHighlightedFields={
                      handleChangeNumHighlightedFields
                    }
                    onSubmitCreate={handleSubmitCreate}
                    onSubmitUpdate={handleSubmitUpdate}
                    {...formModeProps}
                  />
                )}
              </LoadOrError>
            </Tabs.Panel>
          )}
          {showGasConnectionData && (
            <Tabs.Panel value={TABS[1].value}>
              {!activeGasConnectionId ? (
                <Center>
                  <Loader />
                </Center>
              ) : (
                <div className="gas-connection-data-step">
                  <CounterDataView
                    isGasConnectionDataView
                    measurementType={MeasurementType.Single}
                    meterId={activeGasConnectionId}
                    variantId={variantId}
                    onSetRequestData={setCounterUpdateRequestData}
                  />
                  {modeProps.mode === "create" && (
                    <Group justify="flex-end" mt="md">
                      <Button
                        disabled={isSavingGasConnectionData}
                        variant="default"
                        onClick={handleGasConnectionDataCreateModeStepDone}
                      >
                        Überspringen
                      </Button>
                      <Button
                        color="secondary"
                        disabled={isSavingGasConnectionData}
                        loading={isSavingGasConnectionData}
                        onClick={handleSaveGasConnectionData}
                      >
                        Daten speichern
                      </Button>
                    </Group>
                  )}
                </div>
              )}
            </Tabs.Panel>
          )}
        </Tabs>
      </LoadOrError>
    </Modal>
  );
}

export { GasConnectionWizard, GasConnectionWizardProps };
